import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate, useParams, useLocation, Link } from "react-router-dom";
import {
  User,
  Star,
  Heart,
  MessageCircle,
  Users,
  Edit,
  Camera,
  X,
  Award,
  Clock,
  Flame,
  Loader,
  Upload,
  Check,
  Paintbrush,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import ProfileTabs from "../components/ProfileTabs";
import CompactAnimeCard from "../components/CompactAnimeCard";
import ProfileActivityFeed from "../components/ProfileActivityFeed";
import {
  navigateToCanonicalForumThread,
  THREAD_NAV_ORIGIN_COMMUNITY,
} from "../features/posts/canonicalThreadNavigation";

function ProfileHub() {
  const { user, refreshUser, logout } = useAuth();
  const navigate = useNavigate();
  const { userId } = useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const tabFromQuery = queryParams.get("tab");

  const [activeTab, setActiveTab] = useState(tabFromQuery || "overview");
  const [profileUser, setProfileUser] = useState(null);
  const [stats, setStats] = useState(null);

  // Anime favorites list (what your existing /api/favorites returns)
  const [favorites, setFavorites] = useState([]);

  const [friends, setFriends] = useState([]);
  const [badges, setBadges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [bio, setBio] = useState("");
  const [selectedAvatar, setSelectedAvatar] = useState(null);
  const [selectedAvatarUrl, setSelectedAvatarUrl] = useState(null);
  const [selectedBanner, setSelectedBanner] = useState(null);
  const [selectedBackground, setSelectedBackground] = useState(null);
  const [currentTime, setCurrentTime] = useState("");
  const [friendStatus, setFriendStatus] = useState(null); // 'friends', 'pending', 'none'
  const [sendingRequest, setSendingRequest] = useState(false);
  const [areFriends, setAreFriends] = useState(false);
  const [statsCollapsed, setStatsCollapsed] = useState(false);

  // Unified counts for the Favorites tab badge (Anime + ArtHouse + Forums)
  const [favoriteCounts, setFavoriteCounts] = useState({
    anime: 0,
    arthouse: 0,
    forum: 0,
    total: 0,
  });

  const isOwnProfile = !userId || (user && String(user.id) === String(userId));
  const targetUserId = userId || user?.id;
  const canViewFullProfile = isOwnProfile || areFriends;

  // --- Helpers: accept multiple API response shapes safely ---
  const parseListResponse = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (payload && Array.isArray(payload.items)) return payload.items;
    if (payload && Array.isArray(payload.data)) return payload.data;
    if (payload && Array.isArray(payload.results)) return payload.results;
    return [];
  };

  const parseCountResponse = (payload) => {
    if (payload && typeof payload.count === "number") return payload.count;
    return parseListResponse(payload).length;
  };

  const checkFriendship = useCallback(async () => {
    if (!user || !targetUserId || isOwnProfile) return;

    try {
      const res = await fetch(`/api/friends/check/${user.id}/${targetUserId}`);
      if (!res.ok) return;

      const data = await res.json();
      setAreFriends(Boolean(data.areFriends));
      setFriendStatus(data.areFriends ? "friends" : data.status);
    } catch (error) {
      console.error("Error checking friendship:", error);
    }
  }, [user, targetUserId, isOwnProfile]);

  const fetchFavoriteCounts = useCallback(
    async ({ animeCount }) => {
      // If you cannot view full profile, you currently hide the Favorites tab.
      // Keep badge consistent and safe.
      if (!canViewFullProfile) {
        setFavoriteCounts({
          anime: animeCount,
          arthouse: 0,
          forum: 0,
          total: animeCount,
        });
        return;
      }

      const API_BASE = process.env.REACT_APP_API_BASE || "/api";

      try {
        const [arthouseRes, forumRes] = await Promise.all([
          fetch(
            `${API_BASE}/artwork-favorites/user/${targetUserId}?limit=1&offset=0`,
            { credentials: "include" }
          ),
          fetch(`${API_BASE}/users/${targetUserId}/liked-forum-posts`, {
            credentials: "include",
          }),
        ]);

        const arthousePayload = arthouseRes.ok ? await arthouseRes.json() : null;
        const forumPayload = forumRes.ok ? await forumRes.json() : null;

        const arthouseCount = arthousePayload
          ? parseCountResponse(arthousePayload)
          : 0;

        const forumCount = forumPayload ? parseCountResponse(forumPayload) : 0;

        const total = animeCount + arthouseCount + forumCount;

        setFavoriteCounts({
          anime: animeCount,
          arthouse: arthouseCount,
          forum: forumCount,
          total,
        });
      } catch (err) {
        console.error("Error fetching favorite counts:", err);
        setFavoriteCounts({
          anime: animeCount,
          arthouse: 0,
          forum: 0,
          total: animeCount,
        });
      }
    },
    [canViewFullProfile, targetUserId]
  );

  const fetchProfileData = useCallback(async () => {
    try {
      setLoading(true);

      // Always fetch basic stats and badges (public info)
      const statsRes = await fetch(`/api/stats/${targetUserId}/fast`);
      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);
        setProfileUser(statsData.user);
        setBio(statsData.user.bio || "");
        setBadges(statsData.badges || []);
      }

      // Fetch favorites and friends based on privacy
      if (isOwnProfile || areFriends) {
        // Full access: own profile or friend
        const favsRes = await fetch(`/api/favorites/${targetUserId}`);
        if (favsRes.ok) {
          const favsData = await favsRes.json();
          setFavorites(favsData);
          await fetchFavoriteCounts({ animeCount: favsData.length });
        } else {
          setFavorites([]);
          await fetchFavoriteCounts({ animeCount: 0 });
        }

        const friendsRes = await fetch(`/api/friends/${targetUserId}`);
        if (friendsRes.ok) {
          const friendsData = await friendsRes.json();
          setFriends(friendsData);
        }
      } else if (user) {
        // Limited access: non-friend, show only mutual data
        const mutualFavsRes = await fetch(
          `/api/favorites/mutual/${targetUserId}/${user.id}`
        );
        if (mutualFavsRes.ok) {
          const mutualData = await mutualFavsRes.json();
          const mutualAnime = mutualData.mutualFavorites || [];
          setFavorites(mutualAnime);
          await fetchFavoriteCounts({ animeCount: mutualAnime.length });
        } else {
          setFavorites([]);
          await fetchFavoriteCounts({ animeCount: 0 });
        }

        const mutualFriendsRes = await fetch(
          `/api/friends/mutual/${targetUserId}/${user.id}`
        );
        if (mutualFriendsRes.ok) {
          const mutualData = await mutualFriendsRes.json();
          setFriends(mutualData.mutualFriends || []);
        }
      } else {
        setFavorites([]);
        await fetchFavoriteCounts({ animeCount: 0 });
      }
    } catch (error) {
      console.error("Error fetching profile data:", error);
    } finally {
      setLoading(false);
    }
  }, [
    targetUserId,
    isOwnProfile,
    areFriends,
    user,
    fetchFavoriteCounts,
  ]);

  useEffect(() => {
    if (!profileUser?.timezone) return;

    const updateTime = () => {
      try {
        const now = new Date();
        const timeString = now.toLocaleString("en-US", {
          timeZone: profileUser.timezone,
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        });
        setCurrentTime(timeString);
      } catch (error) {
        setCurrentTime("");
      }
    };

    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, [profileUser?.timezone]);

  useEffect(() => {
    if (!user && !userId) {
      navigate("/login");
      return;
    }

    // Check friendship first, then fetch data
    const loadProfile = async () => {
      if (user && !isOwnProfile && targetUserId) {
        await checkFriendship();
      }
      await fetchProfileData();
    };

    loadProfile();
  }, [targetUserId, userId, user, isOwnProfile, navigate, checkFriendship, fetchProfileData]);

  // Keep active tab in sync if query param changes
  useEffect(() => {
    if (tabFromQuery && tabFromQuery !== activeTab) {
      setActiveTab(tabFromQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabFromQuery]);

  const handleAddFriend = async () => {
    if (!user) {
      alert("Please log in to add friends");
      return;
    }

    try {
      setSendingRequest(true);
      const res = await fetch("/api/friends/request", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ friend_id: parseInt(targetUserId, 10) }),
      });

      if (res.ok) {
        setFriendStatus("pending");
        alert("Friend request sent!");
      } else {
        const data = await res.json();
        alert(data.error || "Failed to send friend request");
      }
    } catch (error) {
      console.error("Error sending friend request:", error);
      alert("Failed to send friend request");
    } finally {
      setSendingRequest(false);
    }
  };

  const handleSaveProfile = async () => {
    try {
      let profile_picture_url = profileUser?.profile_picture;
      let banner_image_url = profileUser?.banner_image;
      let background_image_url = profileUser?.background_image;

      // If a pre-made avatar URL is selected, use it
      if (selectedAvatarUrl) {
        profile_picture_url = selectedAvatarUrl;
      }
      // Otherwise, if a custom file was uploaded, upload it
      else if (selectedAvatar) {
        const formData = new FormData();
        formData.append("image", selectedAvatar);
        formData.append("imageType", "avatar");
        const uploadRes = await fetch("/api/upload-image", {
          method: "POST",
          credentials: "include",
          body: formData,
        });
        if (uploadRes.ok) {
          const uploadData = await uploadRes.json();
          profile_picture_url = uploadData?.asset?.url ?? uploadData?.image_url;
        }
      }

      if (selectedBanner) {
        const formData = new FormData();
        formData.append("image", selectedBanner);
        formData.append("imageType", "banner");
        const uploadRes = await fetch("/api/upload-image", {
          method: "POST",
          credentials: "include",
          body: formData,
        });
        if (uploadRes.ok) {
          const uploadData = await uploadRes.json();
          banner_image_url = uploadData?.asset?.url ?? uploadData?.image_url;
        }
      }

      if (selectedBackground) {
        const formData = new FormData();
        formData.append("image", selectedBackground);
        formData.append("imageType", "background");
        const uploadRes = await fetch("/api/upload-image", {
          method: "POST",
          credentials: "include",
          body: formData,
        });
        if (uploadRes.ok) {
          const uploadData = await uploadRes.json();
          background_image_url = uploadData?.asset?.url ?? uploadData?.image_url;
        }
      }

      const updateRes = await fetch(`/api/users/${user.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${user.token}`,
        },
        credentials: "include",
        body: JSON.stringify({
          bio,
          profile_picture: profile_picture_url,
          banner_image: banner_image_url,
          background_image: background_image_url,
        }),
      });

      if (updateRes.ok) {
        if (refreshUser) await refreshUser();

        setShowEditModal(false);
        setSelectedAvatar(null);
        setSelectedAvatarUrl(null);
        setSelectedBanner(null);
        setSelectedBackground(null);

        await fetchProfileData();
      }
    } catch (error) {
      console.error("Error updating profile:", error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 flex items-center justify-center">
        <Loader className="animate-spin text-cyan-400" size={48} />
      </div>
    );
  }

  const level = stats?.level?.current_level || 1;
  const xpToNextLevel = stats?.level?.next_level_xp || 100;
  const currentXP = stats?.level?.current_level_xp || 0;
  const totalXP = stats?.level?.total_xp || 0;
  const dailyStreak = stats?.level?.daily_streak || 0;
  const xpPercentage = ((currentXP / xpToNextLevel) * 100).toFixed(0);
  const earnedBadges = badges.filter((b) => b.earned_at);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white">
      <div className="mx-auto w-full max-w-7xl px-4 pt-6 pb-14">
        {/* Edit button - top right */}
        {isOwnProfile && (
          <div className="flex flex-wrap items-center justify-end gap-3 mb-4">
            <button
              type="button"
              onClick={() => void logout()}
              className="
                h-10 px-4 rounded-xl
                border border-white/15 bg-white/[0.04]
                hover:bg-white/[0.07] transition
                text-sm font-semibold text-white/70
                inline-flex items-center justify-center
              "
            >
              Logout
            </button>
            <button
              type="button"
              onClick={() => setShowEditModal(true)}
              className="
                h-10 px-4 rounded-xl
                border border-sky-300/25 bg-sky-300/10
                hover:bg-sky-300/15 transition
                text-sm font-semibold text-sky-200
                inline-flex items-center justify-center gap-2
              "
            >
              <Edit size={18} />
              Edit Profile
            </button>
          </div>
        )}

        {/* Futuristic profile header */}
        <div className="overflow-hidden rounded-[30px] border border-white/12 bg-white/[0.035] backdrop-blur-xl shadow-[0_30px_140px_rgba(0,0,0,0.55)]">
          {/* banner */}
          <div className="relative h-[180px] sm:h-[220px] md:h-[260px] bg-black/25">
            {profileUser?.banner_image ? (
              <>
                <img
                  src={profileUser.banner_image}
                  alt="Banner"
                  className="absolute inset-0 w-full h-full object-cover opacity-[0.92]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/35 to-black/25" />
              </>
            ) : (
              <>
                <div
                  className="absolute inset-0"
                  style={{
                    background:
                      "radial-gradient(900px 420px at 35% 0%, rgba(6,182,212,0.18), rgba(0,0,0,0.6)), radial-gradient(900px 480px at 100% 20%, rgba(168,85,247,0.12), rgba(0,0,0,0.6))",
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-black/25" />
              </>
            )}

            <div className="pointer-events-none absolute -top-20 left-1/2 h-56 w-[840px] -translate-x-1/2 rounded-full bg-white/10 blur-[40px] opacity-[0.18]" />
          </div>

          {/* body */}
          <div className="relative p-5 sm:p-6">
            <div className="flex items-start justify-between gap-5 flex-wrap">
              <div className="flex items-start gap-4">
                {/* avatar + artprofile button */}
                <div className="-mt-14 sm:-mt-16 shrink-0 flex flex-col items-center gap-3">
                  <div className="h-[92px] w-[92px] sm:h-[108px] sm:w-[108px] rounded-[26px] overflow-hidden border border-white/15 bg-black/30 shadow-[0_18px_60px_rgba(0,0,0,0.55)]">
                    {profileUser?.profile_picture ? (
                      <img
                        src={profileUser.profile_picture}
                        alt={profileUser.username}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="h-full w-full bg-[radial-gradient(circle_at_30%_20%,rgba(6,182,212,0.20),transparent_55%),radial-gradient(circle_at_80%_0%,rgba(168,85,247,0.12),transparent_60%)] flex items-center justify-center text-4xl font-bold">
                        {profileUser?.username?.charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>

                  {isOwnProfile && (
                    <Link
                      to="/dashboard/profile"
                      className="
                        flex items-center justify-center gap-2 px-4 py-3 rounded-xl
                        border border-white/12 bg-white/[0.06]
                        hover:bg-white/[0.10] hover:border-white/20
                        transition-all duration-300 ease-out
                        text-sm font-semibold text-white/85
                        hover:scale-105 active:scale-95
                      "
                    >
                      <Paintbrush size={16} />
                      ArtProfile
                    </Link>
                  )}
                </div>

                <div className="pt-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <div className="text-xl sm:text-2xl font-semibold text-white/92 truncate">
                      {profileUser?.username}
                    </div>

                    {!isOwnProfile && user && (
                      <div className="flex items-center">
                        {friendStatus === "friends" || friendStatus === "accepted" ? (
                          <span className="text-cyan-400 text-sm font-semibold">
                            Friend
                          </span>
                        ) : friendStatus === "pending" ? (
                          <span className="text-yellow-400 text-sm font-semibold">
                            Request Sent
                          </span>
                        ) : (
                          <button
                            onClick={handleAddFriend}
                            disabled={sendingRequest}
                            className="text-cyan-400 hover:text-cyan-300 text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {sendingRequest ? "Sending..." : "Add Friend"}
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="mt-2 text-sm text-white/65 leading-relaxed max-w-[62ch]">
                    {bio || "No bio yet."}
                  </div>

                  {/* Level and XP bar */}
                  <div className="mt-4 flex items-center gap-4">
                    <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/10 backdrop-blur-xl border border-white/10">
                      <Star className="text-yellow-400" size={18} />
                      <span className="font-bold text-sm">Level {level}</span>
                    </div>
                    <div className="flex-1 max-w-md">
                      <div className="bg-white/10 rounded-full h-2.5 overflow-hidden backdrop-blur-xl border border-white/10">
                        <div
                          className="bg-gradient-to-r from-cyan-400 to-purple-400 h-full transition-all duration-300"
                          style={{ width: `${xpPercentage}%` }}
                        />
                      </div>
                      <p className="text-xs text-white/50 mt-1">
                        {currentXP} / {xpToNextLevel} XP
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* could place right-side header actions here later */}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-4 space-y-6">
            <div className="bg-white/[0.06] backdrop-blur-2xl rounded-2xl p-6 border border-white/10 shadow-2xl">
              <h2 className="text-xl font-bold flex items-center gap-2 mb-4">
                <User size={20} className="text-cyan-400" />
                Bio
              </h2>
              <p className="text-white/70 text-sm leading-relaxed">
                {bio || "No bio yet."}
              </p>

              {profileUser?.timezone && currentTime && (
                <div className="mt-4 pt-4 border-t border-white/10">
                  <div className="flex items-center gap-2 text-sm">
                    <Clock size={16} className="text-cyan-400" />
                    <span className="text-white/60">Local Time:</span>
                    <span className="font-semibold text-cyan-400">{currentTime}</span>
                  </div>
                </div>
              )}
            </div>

            <div className="bg-white/[0.06] backdrop-blur-2xl rounded-2xl p-6 border border-white/10 shadow-2xl">
              <button
                onClick={() => setStatsCollapsed(!statsCollapsed)}
                className="w-full flex items-center justify-between mb-4 hover:opacity-80 transition-opacity"
              >
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <Award size={20} className="text-purple-400" />
                  Statistics
                </h2>
                {statsCollapsed ? <ChevronDown size={20} /> : <ChevronUp size={20} />}
              </button>

              {!statsCollapsed && dailyStreak > 0 && (
                <div className="mb-4 p-3 rounded-xl bg-gradient-to-r from-orange-500/20 to-red-500/20 border border-orange-500/30">
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-2 font-semibold text-orange-400">
                      <Flame size={18} className="animate-pulse" />
                      Daily Streak
                    </span>
                    <span className="text-2xl font-bold text-orange-400">
                      {dailyStreak}
                    </span>
                  </div>
                </div>
              )}

              {!statsCollapsed && (
                <>
                  <div className="space-y-3">
                    {canViewFullProfile && (
                      <StatRow
                        icon={Heart}
                        label="Favorites"
                        value={favoriteCounts.total}
                        color="pink"
                      />
                    )}
                    <StatRow
                      icon={MessageCircle}
                      label="Forum Posts"
                      value={stats?.counts?.forum_posts || 0}
                      color="purple"
                    />
                    <StatRow
                      icon={Paintbrush}
                      label="Fan Art"
                      value={stats?.counts?.fan_art_uploads || 0}
                      color="pink"
                    />
                    <StatRow
                      icon={Paintbrush}
                      label="ArtHouse Works"
                      value={stats?.counts?.arthouse_uploads || 0}
                      color="purple"
                    />
                    <StatRow
                      icon={Users}
                      label="Clubs"
                      value={stats?.counts?.clubs_joined || 0}
                      color="green"
                    />
                    <StatRow
                      icon={MessageCircle}
                      label="Comments"
                      value={stats?.counts?.comments || 0}
                      color="purple"
                    />
                    <StatRow
                      icon={Users}
                      label={canViewFullProfile ? "Friends" : "Mutual Friends"}
                      value={friends.length}
                      color="green"
                    />
                    <StatRow
                      icon={Award}
                      label="Badges"
                      value={earnedBadges.length}
                      color="yellow"
                    />
                  </div>

                  <div className="mt-4 pt-4 border-t border-white/10">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-white/60 text-sm font-semibold">XP Breakdown</span>
                      <span className="font-bold text-cyan-400">
                        {totalXP.toLocaleString()} Total
                      </span>
                    </div>
                    
                    {/* XP Breakdown Grid - Rainbow Style */}
                    <div className="space-y-2 text-xs">
                      {/* Reviews */}
                      {(stats?.counts?.reviews || 0) > 0 && (
                        <div className="flex items-center justify-between py-2 px-3 rounded-lg bg-gradient-to-r from-purple-500/20 to-purple-500/10 border border-purple-500/20">
                          <span className="text-purple-200 font-medium">Reviews</span>
                          <div className="flex items-center gap-2">
                            <span className="text-purple-300">{stats.counts.reviews} ×</span>
                            <span className="text-purple-300">~14 XP</span>
                            <span className="text-purple-100 font-bold min-w-[60px] text-right">
                              ≈{(stats.counts.reviews * 14).toLocaleString()}
                            </span>
                          </div>
                        </div>
                      )}
                      
                      {/* Forum Posts */}
                      {(stats?.counts?.forum_posts || 0) > 0 && (
                        <div className="flex items-center justify-between py-2 px-3 rounded-lg bg-gradient-to-r from-blue-500/20 to-blue-500/10 border border-blue-500/20">
                          <span className="text-blue-200 font-medium">Forum Posts</span>
                          <div className="flex items-center gap-2">
                            <span className="text-blue-300">{stats.counts.forum_posts} ×</span>
                            <span className="text-blue-300">~6 XP</span>
                            <span className="text-blue-100 font-bold min-w-[60px] text-right">
                              ≈{(stats.counts.forum_posts * 6).toLocaleString()}
                            </span>
                          </div>
                        </div>
                      )}
                      
                      {/* Fan Art */}
                      {(stats?.counts?.fan_art_uploads || 0) > 0 && (
                        <div className="flex items-center justify-between py-2 px-3 rounded-lg bg-gradient-to-r from-pink-500/20 to-pink-500/10 border border-pink-500/20">
                          <span className="text-pink-200 font-medium">Fan Art</span>
                          <div className="flex items-center gap-2">
                            <span className="text-pink-300">{stats.counts.fan_art_uploads} ×</span>
                            <span className="text-pink-300">10 XP</span>
                            <span className="text-pink-100 font-bold min-w-[60px] text-right">
                              {(stats.counts.fan_art_uploads * 10).toLocaleString()}
                            </span>
                          </div>
                        </div>
                      )}
                      
                      {/* ArtHouse Works */}
                      {(stats?.counts?.arthouse_uploads || 0) > 0 && (
                        <div className="flex items-center justify-between py-2 px-3 rounded-lg bg-gradient-to-r from-cyan-500/20 to-cyan-500/10 border border-cyan-500/20">
                          <span className="text-cyan-200 font-medium">ArtHouse Works</span>
                          <div className="flex items-center gap-2">
                            <span className="text-cyan-300">{stats.counts.arthouse_uploads} ×</span>
                            <span className="text-cyan-300">15 XP</span>
                            <span className="text-cyan-100 font-bold min-w-[60px] text-right">
                              {(stats.counts.arthouse_uploads * 15).toLocaleString()}
                            </span>
                          </div>
                        </div>
                      )}
                      
                      {/* Comments */}
                      {(stats?.counts?.comments || 0) > 0 && (
                        <div className="flex items-center justify-between py-2 px-3 rounded-lg bg-gradient-to-r from-green-500/20 to-green-500/10 border border-green-500/20">
                          <span className="text-green-200 font-medium">Comments</span>
                          <div className="flex items-center gap-2">
                            <span className="text-green-300">{stats.counts.comments} ×</span>
                            <span className="text-green-300">~3 XP</span>
                            <span className="text-green-100 font-bold min-w-[60px] text-right">
                              ≈{(stats.counts.comments * 3).toLocaleString()}
                            </span>
                          </div>
                        </div>
                      )}
                      
                      {/* Forum Likes */}
                      {(stats?.counts?.forum_likes || 0) > 0 && (
                        <div className="flex items-center justify-between py-2 px-3 rounded-lg bg-gradient-to-r from-yellow-500/20 to-yellow-500/10 border border-yellow-500/20">
                          <span className="text-yellow-200 font-medium">Likes Given</span>
                          <div className="flex items-center gap-2">
                            <span className="text-yellow-300">{stats.counts.forum_likes} ×</span>
                            <span className="text-yellow-300">1 XP</span>
                            <span className="text-yellow-100 font-bold min-w-[60px] text-right">
                              {(stats.counts.forum_likes * 1).toLocaleString()}
                            </span>
                          </div>
                        </div>
                      )}
                      
                      {/* Clubs */}
                      {(stats?.counts?.club_posts || 0) > 0 && (
                        <div className="flex items-center justify-between py-2 px-3 rounded-lg bg-gradient-to-r from-orange-500/20 to-orange-500/10 border border-orange-500/20">
                          <span className="text-orange-200 font-medium">Club Posts</span>
                          <div className="flex items-center gap-2">
                            <span className="text-orange-300">{stats.counts.club_posts} ×</span>
                            <span className="text-orange-300">4 XP</span>
                            <span className="text-orange-100 font-bold min-w-[60px] text-right">
                              {(stats.counts.club_posts * 4).toLocaleString()}
                            </span>
                          </div>
                        </div>
                      )}
                      
                      {/* Total from actual XP breakdown */}
                      {stats?.level?.xp_breakdown && (
                        <>
                          <div className="h-px bg-white/10 my-3" />
                          <div className="flex items-center justify-between py-2 px-3 rounded-lg bg-gradient-to-r from-purple-500/20 to-purple-500/10 border border-purple-500/30">
                            <span className="text-purple-200 font-semibold">Total from Reviews</span>
                            <span className="text-purple-100 font-bold">
                              {stats.level.xp_breakdown.reviews?.toLocaleString() || 0}
                            </span>
                          </div>
                          <div className="flex items-center justify-between py-2 px-3 rounded-lg bg-gradient-to-r from-cyan-500/20 to-cyan-500/10 border border-cyan-500/30">
                            <span className="text-cyan-200 font-semibold">Total from Social</span>
                            <span className="text-cyan-100 font-bold">
                              {stats.level.xp_breakdown.social?.toLocaleString() || 0}
                            </span>
                          </div>
                          <div className="flex items-center justify-between py-2 px-3 rounded-lg bg-gradient-to-r from-yellow-500/20 to-yellow-500/10 border border-yellow-500/30">
                            <span className="text-yellow-200 font-semibold">Total from Badges</span>
                            <span className="text-yellow-100 font-bold">
                              {stats.level.xp_breakdown.badges?.toLocaleString() || 0}
                            </span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="lg:col-span-8">
            {/* Privacy Notice for Non-Friends */}
            {!canViewFullProfile && (
              <div className="mb-4 p-4 bg-white/[0.06] backdrop-blur-2xl rounded-2xl border border-white/10">
                <p className="text-sm text-white/60 text-center">
                  Some content is private. Add as friend to see full profile.
                </p>
              </div>
            )}

            <ProfileTabs
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              canViewFullProfile={canViewFullProfile}
              isOwnProfile={isOwnProfile}
              counts={{
                favorites: favoriteCounts.total, // Anime + ArtHouse + Forums
                badges: earnedBadges.length,
                friends: friends.length,
              }}
            />

            <div className="min-h-[400px]">
              {activeTab === "overview" && (
                <OverviewTab
                  stats={stats}
                  favorites={favorites.slice(0, 6)}
                  badges={earnedBadges.slice(0, 6)}
                  friends={friends.slice(0, 6)}
                  totalCounts={{
                    favorites: favoriteCounts.total, // show total everywhere
                    badges: earnedBadges.length,
                    friends: friends.length,
                  }}
                  userId={targetUserId}
                  navigate={navigate}
                  setActiveTab={setActiveTab}
                  canViewFullProfile={canViewFullProfile}
                  isOwnProfile={isOwnProfile}
                />
              )}

              {activeTab === "favorites" && canViewFullProfile && (
                <FavoritesTab favorites={favorites} userId={targetUserId} navigate={navigate} />
              )}
              {activeTab === "activity" && isOwnProfile && <ActivityTab userId={targetUserId} />}
              {activeTab === "badges" && <BadgesTab userId={targetUserId} />}
              {activeTab === "friends" && (
                <div>
                  {!canViewFullProfile && (
                    <div className="mb-4 text-center">
                      <p className="text-sm text-cyan-400">
                        Showing {friends.length} mutual friend(s)
                      </p>
                    </div>
                  )}
                  <FriendsTab friends={friends} navigate={navigate} isOwnProfile={isOwnProfile} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {showEditModal && (
        <EditProfileModal
          bio={bio}
          setBio={setBio}
          selectedAvatar={selectedAvatar}
          setSelectedAvatar={setSelectedAvatar}
          selectedAvatarUrl={selectedAvatarUrl}
          setSelectedAvatarUrl={setSelectedAvatarUrl}
          selectedBanner={selectedBanner}
          setSelectedBanner={setSelectedBanner}
          selectedBackground={selectedBackground}
          setSelectedBackground={setSelectedBackground}
          currentAvatar={profileUser?.profile_picture}
          onClose={() => setShowEditModal(false)}
          onSave={handleSaveProfile}
        />
      )}
    </div>
  );
}

const StatRow = ({ icon: Icon, label, value, color }) => {
  const colorClasses = {
    pink: "text-pink-400",
    purple: "text-purple-400",
    green: "text-green-400",
    yellow: "text-yellow-400",
  };

  return (
    <div className="flex items-center justify-between py-2">
      <div className="flex items-center gap-2">
        <Icon size={16} className={colorClasses[color]} />
        <span className="text-white/70 text-sm">{label}</span>
      </div>
      <span className="font-semibold text-white/90">{value}</span>
    </div>
  );
};

const OverviewTab = ({
  stats,
  favorites,
  badges,
  friends,
  totalCounts,
  userId,
  navigate,
  setActiveTab,
  canViewFullProfile = true,
  isOwnProfile = false,
}) => {
  return (
    <div className="space-y-6">
      <div
        className={`grid ${
          canViewFullProfile ? "grid-cols-2 md:grid-cols-4" : "grid-cols-2 md:grid-cols-3"
        } gap-4`}
      >
        {canViewFullProfile && (
          <QuickStatCard
            label="Favorites"
            value={totalCounts.favorites}
            icon={Heart}
            color="pink"
            onClick={() => setActiveTab("favorites")}
          />
        )}
        <QuickStatCard
          label="Badges"
          value={totalCounts.badges}
          icon={Award}
          color="yellow"
          onClick={() => setActiveTab("badges")}
        />
        <QuickStatCard
          label="Friends"
          value={totalCounts.friends}
          icon={Users}
          color="green"
          onClick={() => setActiveTab("friends")}
        />
        <QuickStatCard
          label="Forum Posts"
          value={stats?.counts?.forum_posts || 0}
          icon={MessageCircle}
          color="purple"
        />
      </div>

      {canViewFullProfile && favorites.length > 0 && (
        <div className="bg-white/[0.06] backdrop-blur-2xl rounded-2xl p-6 border border-white/10">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold">Recent Favorites</h3>
            <button
              onClick={() => setActiveTab("favorites")}
              className="text-sm text-cyan-400 hover:text-cyan-300 transition-colors"
            >
              View All →
            </button>
          </div>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
            {favorites.map((fav) => (
              <CompactAnimeCard key={fav.anime_id} anime={fav} />
            ))}
          </div>
        </div>
      )}

      {isOwnProfile && (
        <div className="bg-white/[0.06] backdrop-blur-2xl rounded-2xl p-6 border border-white/10">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold">Recent Activity</h3>
            <button
              onClick={() => setActiveTab("activity")}
              className="text-sm text-cyan-400 hover:text-cyan-300 transition-colors"
            >
              View All →
            </button>
          </div>
          <ProfileActivityFeed userId={userId} showPagination={false} />
        </div>
      )}
    </div>
  );
};

const QuickStatCard = ({ label, value, icon: Icon, color, onClick }) => {
  const colorClasses = {
    pink: "from-pink-500/20 to-pink-600/10 border-pink-500/30",
    purple: "from-purple-500/20 to-purple-600/10 border-purple-500/30",
    green: "from-green-500/20 to-green-600/10 border-green-500/30",
    yellow: "from-yellow-500/20 to-yellow-600/10 border-yellow-500/30",
  };

  return (
    <div
      onClick={onClick}
      className={`bg-gradient-to-br ${colorClasses[color]} border rounded-xl p-4 ${
        onClick ? "cursor-pointer hover:scale-105" : ""
      } transition-transform duration-200`}
    >
      <Icon size={24} className="text-white/80 mb-2" />
      <div className="text-2xl font-bold text-white">{value}</div>
      <div className="text-sm text-white/60">{label}</div>
    </div>
  );
};

const FavoritesTab = ({ favorites, userId, navigate }) => {
  const [favSubTab, setFavSubTab] = useState("anime");

  const [arthouseFavs, setArthouseFavs] = useState([]);
  const [forumFavs, setForumFavs] = useState([]);

  const [arthouseCount, setArthouseCount] = useState(0);
  const [forumCount, setForumCount] = useState(0);

  const [loading, setLoading] = useState(false);

  // --- Helpers: accept multiple API response shapes safely ---
  const parseListResponse = (payload) => {
    // Accept: [] | { items: [] } | { data: [] } | { results: [] }
    if (Array.isArray(payload)) return payload;
    if (payload && Array.isArray(payload.items)) return payload.items;
    if (payload && Array.isArray(payload.data)) return payload.data;
    if (payload && Array.isArray(payload.results)) return payload.results;
    return [];
  };

  const parseCountResponse = (payload) => {
    if (payload && typeof payload.count === "number") return payload.count;
    const items = parseListResponse(payload);
    return items.length;
  };

  const resolveArtFields = (art) => {
    const artId = art?.art_id ?? art?.artwork_id ?? art?.id ?? null;

    const img =
      art?.image_url ??
      art?.full_image_url ??
      art?.thumbnail_url ??
      art?.thumb_url ??
      art?.image ??
      null;

    const title = art?.title ?? art?.name ?? "Untitled";

    // Your backend list returns artist_id (no artists join for stability)
    const artist =
      art?.artist_name ??
      art?.display_name ??
      art?.artist ??
      art?.creator_name ??
      (art?.artist_id ? `Artist #${art.artist_id}` : "Unknown Artist");

    return { artId, img, title, artist };
  };

  // Fetch counts immediately on mount
  useEffect(() => {
    if (userId) fetchCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // Fetch full data when tabs are clicked
  useEffect(() => {
    if (!userId) return;

    if (favSubTab === "arthouse") {
      fetchArthouseFavorites();
    } else if (favSubTab === "forum") {
      fetchForumFavorites();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [favSubTab, userId]);

  const fetchCounts = async () => {
    try {
      const API_BASE = process.env.REACT_APP_API_BASE || "/api";

      // ArtHouse count + items wrapper
      const arthouseRes = await fetch(
        `${API_BASE}/artwork-favorites/user/${userId}?limit=1&offset=0`,
        {
          credentials: "include",
        }
      );

      if (arthouseRes.ok) {
        const payload = await arthouseRes.json();
        setArthouseCount(parseCountResponse(payload));
      } else {
        console.error("ArtHouse count failed:", arthouseRes.status);
        setArthouseCount(0);
      }

      // Forums count (existing)
      const forumRes = await fetch(`${API_BASE}/users/${userId}/liked-forum-posts`, {
        credentials: "include",
      });

      if (forumRes.ok) {
        const forumPayload = await forumRes.json();
        setForumCount(parseCountResponse(forumPayload));
      } else {
        console.error("Forum count failed:", forumRes.status);
        setForumCount(0);
      }
    } catch (error) {
      console.error("Error fetching counts:", error);
      setArthouseCount(0);
      setForumCount(0);
    }
  };

  const fetchArthouseFavorites = async () => {
    try {
      setLoading(true);
      const API_BASE = process.env.REACT_APP_API_BASE || "/api";

      const res = await fetch(
        `${API_BASE}/artwork-favorites/user/${userId}?limit=60&offset=0`,
        {
          credentials: "include",
        }
      );

      if (!res.ok) {
        console.error("Error fetching arthouse favorites:", res.status);
        setArthouseFavs([]);
        return;
      }

      const payload = await res.json();
      const items = parseListResponse(payload);
      setArthouseFavs(items);
      setArthouseCount(parseCountResponse(payload)); // use payload.count if present
    } catch (error) {
      console.error("Error fetching arthouse favorites:", error);
      setArthouseFavs([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchForumFavorites = async () => {
    try {
      setLoading(true);
      const API_BASE = process.env.REACT_APP_API_BASE || "/api";

      const res = await fetch(`${API_BASE}/users/${userId}/liked-forum-posts`, {
        credentials: "include",
      });

      if (!res.ok) {
        console.error("Error fetching forum favorites:", res.status);
        setForumFavs([]);
        return;
      }

      const payload = await res.json();
      const items = parseListResponse(payload);
      setForumFavs(items);
      setForumCount(items.length);
    } catch (error) {
      console.error("Error fetching forum favorites:", error);
      setForumFavs([]);
    } finally {
      setLoading(false);
    }
  };

  const arthouseDisplayCount =
    arthouseFavs.length > 0 ? arthouseFavs.length : arthouseCount;
  const forumDisplayCount = forumFavs.length > 0 ? forumFavs.length : forumCount;

  return (
    <div className="bg-white/[0.06] backdrop-blur-2xl rounded-2xl p-6 border border-white/10">
      <div className="flex items-center gap-4 mb-6 border-b border-white/10 pb-4">
        <button
          onClick={() => setFavSubTab("anime")}
          className={`px-4 py-2 rounded-lg font-semibold transition-all ${
            favSubTab === "anime"
              ? "bg-cyan-500/20 text-cyan-400 border border-cyan-500/30"
              : "text-white/60 hover:text-white/80 hover:bg-white/5"
          }`}
        >
          Anime ({favorites.length})
        </button>

        <button
          onClick={() => setFavSubTab("arthouse")}
          className={`px-4 py-2 rounded-lg font-semibold transition-all ${
            favSubTab === "arthouse"
              ? "bg-cyan-500/20 text-cyan-400 border border-cyan-500/30"
              : "text-white/60 hover:text-white/80 hover:bg-white/5"
          }`}
        >
          ArtHouse ({arthouseDisplayCount})
        </button>

        <button
          onClick={() => setFavSubTab("forum")}
          className={`px-4 py-2 rounded-lg font-semibold transition-all ${
            favSubTab === "forum"
              ? "bg-cyan-500/20 text-cyan-400 border border-cyan-500/30"
              : "text-white/60 hover:text-white/80 hover:bg-white/5"
          }`}
        >
          Forums ({forumDisplayCount})
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader className="animate-spin text-cyan-400" size={32} />
        </div>
      ) : (
        <>
          {favSubTab === "anime" &&
            (favorites.length === 0 ? (
              <div className="text-center py-12">
                <Heart size={48} className="mx-auto text-white/20 mb-4" />
                <div className="text-white/40 text-lg mb-2">
                  No anime favorites yet
                </div>
                <p className="text-white/30 text-sm">
                  Start adding anime to your favorites!
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {favorites.map((fav) => (
                  <CompactAnimeCard key={fav.anime_id} anime={fav} />
                ))}
              </div>
            ))}

          {favSubTab === "arthouse" &&
            (arthouseFavs.length === 0 ? (
              <div className="text-center py-12">
                <Heart size={48} className="mx-auto text-white/20 mb-4" />
                <div className="text-white/40 text-lg mb-2">
                  No ArtHouse favorites yet
                </div>
                <p className="text-white/30 text-sm">Start liking artwork!</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {arthouseFavs.map((art, idx) => {
                  const { artId, img, title, artist } = resolveArtFields(art);
                  const key = artId ?? `${idx}-${title}`;

                  return (
                    <div
                      key={key}
                      onClick={() => {
                        if (!artId) return;
                        navigate(`/arthouse/art/${artId}`);
                      }}
                      className={`group relative rounded-xl overflow-hidden transition-all duration-300 hover:scale-105 hover:shadow-2xl ${
                        artId ? "cursor-pointer" : "cursor-default opacity-60"
                      }`}
                      title={!artId ? "Missing artwork id from API" : undefined}
                    >
                      <div className="relative aspect-square overflow-hidden bg-white/5">
                        {img ? (
                          <img
                            src={img}
                            alt={title}
                            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                            loading="lazy"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-white/30 text-sm">
                            No image
                          </div>
                        )}

                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                        <div className="absolute bottom-0 left-0 right-0 p-3 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                          <h3 className="text-sm font-semibold text-white line-clamp-2">
                            {title}
                          </h3>
                          <p className="text-xs text-white/70 mt-1">
                            by {artist}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}

          {favSubTab === "forum" &&
            (forumFavs.length === 0 ? (
              <div className="text-center py-12">
                <Heart size={48} className="mx-auto text-white/20 mb-4" />
                <div className="text-white/40 text-lg mb-2">
                  No forum post favorites yet
                </div>
                <p className="text-white/30 text-sm">
                  Start liking forum posts!
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {forumFavs.map((post) => (
                  <div
                    key={post.post_id}
                    onClick={() =>
                      navigateToCanonicalForumThread(navigate, post, {
                        origin: THREAD_NAV_ORIGIN_COMMUNITY,
                      })
                    }
                    className="bg-white/[0.04] border border-white/10 rounded-xl p-4 hover:bg-white/[0.08] hover:border-cyan-400/30 transition-all cursor-pointer"
                  >
                    <h3 className="text-lg font-semibold text-white mb-2">
                      {post.title}
                    </h3>
                    <p className="text-white/60 text-sm line-clamp-2 mb-3">
                      {post.content}
                    </p>
                    <div className="flex items-center gap-4 text-xs text-white/50">
                      <span>by {post.author}</span>
                      <span>•</span>
                      <span>{post.total_likes} likes</span>
                      <span>•</span>
                      <span>{post.total_comments} comments</span>
                    </div>
                  </div>
                ))}
              </div>
            ))}
        </>
      )}
    </div>
  );
};

const ActivityTab = ({ userId }) => {
  return (
    <div className="bg-white/[0.06] backdrop-blur-2xl rounded-2xl p-6 border border-white/10">
      <h2 className="text-xl font-bold mb-6">Activity Feed</h2>
      <ProfileActivityFeed userId={userId} />
    </div>
  );
};

const BadgesTab = ({ userId }) => {
  const [badgeSubTab, setBadgeSubTab] = useState("badges");
  const [allBadges, setAllBadges] = useState([]);
  const [allPerks, setAllPerks] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch both on mount to show accurate counts
  useEffect(() => {
    if (userId) {
      fetchAllBadges();
      fetchAllPerks();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const fetchAllBadges = async () => {
    try {
      setLoading(true);
      // Fetch all available badges
      const allBadgesRes = await fetch("/api/badges/all");
      const allBadgesData = (allBadgesRes.ok && (await allBadgesRes.json())) || [];

      // Fetch user's earned badges
      const earnedBadgesRes = await fetch(`/api/badges/${userId}`);
      const earnedBadgesData =
        (earnedBadgesRes.ok && (await earnedBadgesRes.json())) || [];

      // Mark which badges are earned
      const earnedIds = new Set(earnedBadgesData.map((b) => b.badge_id));
      const badgesWithStatus = allBadgesData.map((badge) => ({
        ...badge,
        earned: earnedIds.has(badge.badge_id),
        earned_at: earnedBadgesData.find((eb) => eb.badge_id === badge.badge_id)
          ?.earned_at,
      }));

      setAllBadges(badgesWithStatus);
    } catch (error) {
      console.error("Error fetching badges:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllPerks = async () => {
    try {
      setLoading(true);
      const res = await fetch(`/api/perks/user/${userId}`);
      if (res.ok) {
        const data = await res.json();
        setAllPerks(data.perks || []);
      }
    } catch (error) {
      console.error("Error fetching perks:", error);
    } finally {
      setLoading(false);
    }
  };

  const earnedBadges = allBadges.filter((b) => b.earned);
  const unearnedBadges = allBadges.filter((b) => !b.earned);
  const earnedPerks = allPerks.filter((p) => p.unlocked);
  const unearnedPerks = allPerks.filter((p) => !p.unlocked);

  return (
    <div className="bg-white/[0.06] backdrop-blur-2xl rounded-2xl p-6 border border-white/10">
      <div className="flex items-center gap-4 mb-6 border-b border-white/10 pb-4">
        <button
          onClick={() => setBadgeSubTab("badges")}
          className={`px-4 py-2 rounded-lg font-semibold transition-all ${
            badgeSubTab === "badges"
              ? "bg-cyan-500/20 text-cyan-400 border border-cyan-500/30"
              : "text-white/60 hover:text-white/80 hover:bg-white/5"
          }`}
        >
          Badges ({earnedBadges.length}/{allBadges.length})
        </button>
        <button
          onClick={() => setBadgeSubTab("perks")}
          className={`px-4 py-2 rounded-lg font-semibold transition-all ${
            badgeSubTab === "perks"
              ? "bg-cyan-500/20 text-cyan-400 border border-cyan-500/30"
              : "text-white/60 hover:text-white/80 hover:bg-white/5"
          }`}
        >
          Perks ({earnedPerks.length}/{allPerks.length})
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader className="animate-spin text-cyan-400" size={32} />
        </div>
      ) : (
        <>
          {badgeSubTab === "badges" && (
            <div className="space-y-6">
              {/* Earned Badges */}
              {earnedBadges.length > 0 && (
                <div>
                  <h3 className="text-lg font-bold mb-4 text-cyan-400">
                    Earned Badges ({earnedBadges.length})
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                    {earnedBadges.map((badge) => (
                      <div
                        key={badge.badge_id}
                        className="bg-gradient-to-br from-cyan-500/10 to-purple-500/10 border border-cyan-400/30 rounded-xl p-4 hover:from-cyan-500/15 hover:to-purple-500/15 transition-all"
                      >
                        <div className="text-4xl mb-2">{badge.icon || ""}</div>
                        <div className="text-sm font-semibold text-white/90">
                          {badge.name}
                        </div>
                        <div className="text-xs text-white/50 mt-1 line-clamp-2">
                          {badge.description}
                        </div>
                        {badge.xp_reward > 0 && (
                          <div className="mt-2 text-xs text-cyan-400">
                            +{badge.xp_reward} XP
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Unearned Badges */}
              {unearnedBadges.length > 0 && (
                <div>
                  <h3 className="text-lg font-bold mb-4 text-white/60">
                    Locked Badges ({unearnedBadges.length})
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                    {unearnedBadges.map((badge) => (
                      <div
                        key={badge.badge_id}
                        className="bg-white/[0.04] border border-white/10 rounded-xl p-4 opacity-50 hover:opacity-70 transition-all"
                      >
                        <div className="text-4xl mb-2 grayscale">
                          {badge.icon || ""}
                        </div>
                        <div className="text-sm font-semibold text-white/70">
                          {badge.name}
                        </div>
                        <div className="text-xs text-white/40 mt-1 line-clamp-2">
                          {badge.description}
                        </div>
                        {badge.xp_reward > 0 && (
                          <div className="mt-2 text-xs text-white/40">
                            +{badge.xp_reward} XP
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {allBadges.length === 0 && (
                <div className="text-center py-12">
                  <Award size={48} className="mx-auto text-white/20 mb-4" />
                  <div className="text-white/40 text-lg mb-2">
                    No badges available
                  </div>
                </div>
              )}
            </div>
          )}

          {badgeSubTab === "perks" && (
            <div className="space-y-6">
              {/* Earned Perks */}
              {earnedPerks.length > 0 && (
                <div>
                  <h3 className="text-lg font-bold mb-4 text-cyan-400">
                    Unlocked Perks ({earnedPerks.length})
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {earnedPerks.map((perk) => (
                      <div
                        key={perk.perk_id}
                        className="bg-gradient-to-br from-cyan-500/10 to-purple-500/10 border border-cyan-400/30 rounded-xl p-4 hover:from-cyan-500/15 hover:to-purple-500/15 transition-all"
                      >
                        <div className="flex items-start gap-3">
                          <div className="text-3xl flex-shrink-0">
                            {perk.icon || ""}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-white/90">
                              {perk.perk_name}
                            </div>
                            <div className="text-xs text-white/50 mt-1">
                              {perk.description}
                            </div>
                            <div className="mt-2 flex items-center gap-2">
                              <span className="text-xs text-cyan-400">
                                Level {perk.level_required}
                              </span>
                              {perk.coming_soon && (
                                <span className="text-xs text-yellow-400 bg-yellow-400/10 px-2 py-0.5 rounded">
                                  Currently Under Construction
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Unearned Perks */}
              {unearnedPerks.length > 0 && (
                <div>
                  <h3 className="text-lg font-bold mb-4 text-white/60">
                    Locked Perks ({unearnedPerks.length})
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {unearnedPerks.map((perk) => (
                      <div
                        key={perk.perk_id}
                        className="bg-white/[0.04] border border-white/10 rounded-xl p-4 opacity-50 hover:opacity-70 transition-all"
                      >
                        <div className="flex items-start gap-3">
                          <div className="text-3xl flex-shrink-0 grayscale">
                            {perk.icon || ""}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-white/70">
                              {perk.perk_name}
                            </div>
                            <div className="text-xs text-white/40 mt-1">
                              {perk.description}
                            </div>
                            <div className="mt-2 flex items-center gap-2">
                              <span className="text-xs text-white/40">
                                Level {perk.level_required}
                              </span>
                              {perk.coming_soon && (
                                <span className="text-xs text-white/40 bg-white/5 px-2 py-0.5 rounded">
                                  Currently Under Construction
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {allPerks.length === 0 && (
                <div className="text-center py-12">
                  <Award size={48} className="mx-auto text-white/20 mb-4" />
                  <div className="text-white/40 text-lg mb-2">No perks available</div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

const FriendsTab = ({ friends, navigate, isOwnProfile = false }) => {
  const { user } = useAuth();
  const [pendingRequests, setPendingRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [sendingRequestTo, setSendingRequestTo] = useState(null);

  useEffect(() => {
    // Only fetch pending requests if viewing own profile
    if (user && isOwnProfile) {
      fetchPendingRequests();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isOwnProfile]);

  const fetchPendingRequests = async () => {
    try {
      const res = await fetch("/api/friends/requests", {
        credentials: "include",
      });

      if (res.ok) {
        const data = await res.json();
        setPendingRequests(data.requests || []);
      }
    } catch (error) {
      console.error("Error fetching friend requests:", error);
    }
  };

  const handleAccept = async (friendshipId) => {
    try {
      setLoading(true);
      const res = await fetch(`/api/friends/accept/${friendshipId}`, {
        method: "PUT",
        credentials: "include",
      });

      if (res.ok) {
        alert("Friend request accepted!");
        fetchPendingRequests();
        window.location.reload();
      } else {
        const data = await res.json();
        alert(data.error || "Failed to accept request");
      }
    } catch (error) {
      console.error("Error accepting request:", error);
      alert("Failed to accept request");
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async (friendshipId) => {
    try {
      setLoading(true);
      const res = await fetch(`/api/friends/reject/${friendshipId}`, {
        method: "PUT",
        credentials: "include",
      });

      if (res.ok) {
        alert("Friend request rejected");
        fetchPendingRequests();
      } else {
        const data = await res.json();
        alert(data.error || "Failed to reject request");
      }
    } catch (error) {
      console.error("Error rejecting request:", error);
      alert("Failed to reject request");
    } finally {
      setLoading(false);
    }
  };

  // Debounced search function
  useEffect(() => {
    if (!isOwnProfile || !searchQuery.trim()) {
      setSearchResults([]);
      return;
    }

    const timeoutId = setTimeout(async () => {
      try {
        setSearching(true);
        const res = await fetch(
          `/api/friends/search?query=${encodeURIComponent(searchQuery)}`,
          { credentials: "include" }
        );

        if (res.ok) {
          const data = await res.json();
          setSearchResults(data.users || []);
        }
      } catch (error) {
        console.error("Error searching users:", error);
      } finally {
        setSearching(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, isOwnProfile]);

  const handleSendRequest = async (friendId) => {
    try {
      setSendingRequestTo(friendId);
      const res = await fetch("/api/friends/request", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ friend_id: friendId }),
      });

      if (res.ok) {
        alert("Friend request sent!");
        setSearchResults((prev) =>
          prev.map((u) =>
            u.user_id === friendId ? { ...u, friendship_status: "pending" } : u
          )
        );
      } else {
        const data = await res.json();
        alert(data.error || "Failed to send friend request");
      }
    } catch (error) {
      console.error("Error sending friend request:", error);
      alert("Failed to send friend request");
    } finally {
      setSendingRequestTo(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Friend Search - only show on own profile */}
      {isOwnProfile && (
        <div className="bg-white/[0.06] backdrop-blur-2xl rounded-2xl p-6 border border-white/10">
          <h2 className="text-xl font-bold mb-4 text-cyan-400">Add Friends</h2>
          <div className="relative">
            <input
              type="text"
              placeholder="Search by username..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-cyan-400/50 transition-colors"
            />
            {searching && (
              <div className="absolute right-3 top-3.5">
                <Loader className="animate-spin text-cyan-400" size={20} />
              </div>
            )}
          </div>

          {/* Search Results */}
          {searchQuery.trim() && searchResults.length > 0 && (
            <div className="mt-4 space-y-2">
              {searchResults.map((result) => (
                <div
                  key={result.user_id}
                  className="bg-white/[0.04] border border-white/10 rounded-xl p-4 flex items-center justify-between hover:bg-white/[0.06] transition-colors"
                >
                  <div
                    className="flex items-center gap-3 cursor-pointer"
                    onClick={() => navigate(`/hub/${result.user_id}`)}
                  >
                    {result.profile_picture ? (
                      <img
                        src={result.profile_picture}
                        alt={result.username}
                        className="w-12 h-12 rounded-lg object-cover object-center border border-white/20"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-lg bg-white/10 flex items-center justify-center text-xl font-bold border border-white/20">
                        {result.username?.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div>
                      <div className="font-semibold text-white/90">
                        {result.username}
                      </div>
                      <div className="text-xs text-white/50">
                        Level {result.level}
                      </div>
                    </div>
                  </div>
                  <div>
                    {result.friendship_status === "accepted" ? (
                      <span className="text-cyan-400 text-sm font-semibold">
                        Friend
                      </span>
                    ) : result.friendship_status === "pending" ? (
                      <span className="text-yellow-400 text-sm font-semibold">
                        Request Sent
                      </span>
                    ) : (
                      <button
                        onClick={() => handleSendRequest(result.user_id)}
                        disabled={sendingRequestTo === result.user_id}
                        className="px-4 py-2 rounded-lg bg-cyan-500/20 border border-cyan-400/30 text-cyan-400 hover:bg-cyan-500/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm font-semibold"
                      >
                        {sendingRequestTo === result.user_id ? "Sending..." : "Add Friend"}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* No results message */}
          {searchQuery.trim() && !searching && searchResults.length === 0 && (
            <div className="mt-4 text-center py-6 text-white/40 text-sm">
              No users found matching "{searchQuery}"
            </div>
          )}
        </div>
      )}

      {/* Pending Requests - only show on own profile */}
      {isOwnProfile && pendingRequests.length > 0 && (
        <div className="bg-white/[0.06] backdrop-blur-2xl rounded-2xl p-6 border border-white/10">
          <h2 className="text-xl font-bold mb-6 text-cyan-400">
            Pending Requests ({pendingRequests.length})
          </h2>
          <div className="space-y-3">
            {pendingRequests.map((request) => (
              <div
                key={request.friendship_id}
                className="bg-white/[0.04] border border-white/10 rounded-xl p-4 flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  {request.profile_picture ? (
                    <img
                      src={request.profile_picture}
                      alt={request.username}
                      className="w-12 h-12 rounded-lg object-cover object-center border border-white/20"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-lg bg-white/10 flex items-center justify-center text-xl font-bold border border-white/20">
                      {request.username?.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div>
                    <div className="font-semibold text-white/90">
                      {request.username}
                    </div>
                    <div className="text-xs text-white/50">Friend request</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleAccept(request.friendship_id)}
                    disabled={loading}
                    className="px-4 py-2 rounded-lg bg-cyan-500/20 border border-cyan-400/30 text-cyan-400 hover:bg-cyan-500/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm font-semibold"
                  >
                    Accept
                  </button>
                  <button
                    onClick={() => handleReject(request.friendship_id)}
                    disabled={loading}
                    className="px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white/70 hover:bg-white/10 transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                  >
                    Reject
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Friends List */}
      <div className="bg-white/[0.06] backdrop-blur-2xl rounded-2xl p-6 border border-white/10">
        <h2 className="text-xl font-bold mb-6">Friends ({friends.length})</h2>
        {friends.length === 0 ? (
          <div className="text-center py-12">
            <Users size={48} className="mx-auto text-white/20 mb-4" />
            <div className="text-white/40 text-lg mb-2">No friends yet</div>
            <p className="text-white/30 text-sm">Connect with other anime fans!</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {friends.map((friend) => (
              <div
                key={friend.user_id}
                onClick={() => navigate(`/hub/${friend.user_id}`)}
                className="bg-white/[0.04] rounded-xl p-4 hover:bg-white/[0.08] hover:border-cyan-400/30 border border-white/10 transition-all cursor-pointer"
              >
                {friend.profile_picture ? (
                  <img
                    src={friend.profile_picture}
                    alt={friend.username}
                    className="w-16 h-16 rounded-lg object-cover object-center border border-white/20 mb-3"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-lg bg-white/10 flex items-center justify-center text-2xl font-bold border border-white/20 mb-3">
                    {friend.username?.charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="font-semibold text-sm truncate text-white/90">
                  {friend.username}
                </div>
                <div className="text-xs text-white/50">Level {friend.level || 1}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const EditProfileModal = ({
  bio,
  setBio,
  selectedAvatar,
  setSelectedAvatar,
  selectedAvatarUrl,
  setSelectedAvatarUrl,
  selectedBanner,
  setSelectedBanner,
  selectedBackground,
  setSelectedBackground,
  onClose,
  onSave,
  currentAvatar,
}) => {
  const [isUploading, setIsUploading] = useState(false);

  const avatars = [
    "/avatars/avatar1.png",
    "/avatars/avatar2.png",
    "/avatars/avatar3.png",
    "/avatars/avatar4.png",
    "/avatars/avatar5.png",
    "/avatars/avatar6.png",
    "/avatars/avatar7.png",
    "/avatars/avatar8.png",
    "/avatars/avatar9.png",
    "/avatars/avatar10.png",
    "/avatars/avatar11.png",
    "/avatars/avatar12.png",
    "/avatars/avatar13.jpg",
  ];

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      alert("Please select a valid image file (JPEG, PNG, GIF, or WebP)");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert("File size must be less than 5MB");
      return;
    }

    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append("image", file);
      formData.append("imageType", "avatar");

      const API_BASE = process.env.REACT_APP_API_BASE || "/api";
      const response = await fetch(`${API_BASE}/upload-image`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        const uploadedAvatarUrl = data?.asset?.url ?? data?.image_url ?? null;
        if (!uploadedAvatarUrl) {
          throw new Error("Upload succeeded but no image URL was returned");
        }
        setSelectedAvatarUrl(uploadedAvatarUrl);
        setSelectedAvatar(null);
      } else {
        const error = await response.json();
        alert(`Upload failed: ${error.error}`);
      }
    } catch (error) {
      console.error("Upload error:", error);
      alert("Failed to upload image. Please try again.");
    } finally {
      setIsUploading(false);
      event.target.value = "";
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-md flex items-center justify-center p-4 overflow-y-auto">
      <div className="max-w-3xl w-full bg-white/[0.06] backdrop-blur-2xl rounded-2xl border border-white/10 shadow-2xl my-8">
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <h2 className="text-2xl font-bold">Edit Profile</h2>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
            <X size={24} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-white/70 mb-2">Bio</label>
            <textarea
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:border-cyan-400/50 focus:outline-none resize-none"
              rows={4}
              placeholder="Tell us about yourself..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-white/70 mb-3">Profile Picture</label>

            <div className="grid grid-cols-4 sm:grid-cols-5 gap-3 mb-4">
              {avatars.map((avatar, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => {
                    setSelectedAvatarUrl(avatar);
                    setSelectedAvatar(null);
                  }}
                  className={`relative rounded-full overflow-hidden border-4 transition-all hover:scale-110 aspect-square ${
                    selectedAvatarUrl === avatar
                      ? "border-cyan-500 shadow-lg shadow-cyan-500/50"
                      : "border-white/20 hover:border-cyan-400"
                  }`}
                >
                  <img
                    src={avatar}
                    alt={`Avatar ${index + 1}`}
                    className="w-full h-full object-cover object-center bg-white aspect-square"
                  />
                  {selectedAvatarUrl === avatar && (
                    <div className="absolute inset-0 bg-cyan-500/30 flex items-center justify-center">
                      <Check className="text-white" size={24} strokeWidth={3} />
                    </div>
                  )}
                </button>
              ))}

              <label className="relative rounded-full overflow-hidden border-4 border-dashed border-white/30 hover:border-cyan-400 transition-all hover:scale-110 aspect-square cursor-pointer bg-white/5 flex items-center justify-center">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="hidden"
                  disabled={isUploading}
                />
                {isUploading ? (
                  <div className="text-white text-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-cyan-400 mx-auto mb-1"></div>
                    <p className="text-[10px]">Uploading...</p>
                  </div>
                ) : (
                  <div className="text-white/60 text-center">
                    <Upload size={20} className="mx-auto mb-1" />
                    <p className="text-[10px]">Upload</p>
                    <p className="text-[8px] text-white/40 mt-0.5">512x512</p>
                  </div>
                )}
              </label>
            </div>

            {selectedAvatarUrl && (
              <div className="flex items-center gap-3 p-3 bg-white/5 rounded-xl border border-white/10">
                <img
                  src={selectedAvatarUrl}
                  alt="Selected avatar"
                  className="w-12 h-12 rounded-full border-2 border-cyan-500 object-cover object-center"
                />
                <p className="text-xs text-white/60">This will be your new profile picture</p>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-white/70 mb-2">
              Banner Image{" "}
              <span className="text-xs text-white/40 font-normal">(Preferred: 1920x400)</span>
            </label>
            <div className="flex items-center gap-4">
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setSelectedBanner(e.target.files[0])}
                className="hidden"
                id="banner-upload"
              />
              <label
                htmlFor="banner-upload"
                className="px-4 py-2 bg-white/10 hover:bg-white/15 border border-white/20 rounded-lg cursor-pointer transition-colors flex items-center gap-2"
              >
                <Camera size={18} />
                Choose File
              </label>
              {selectedBanner && <span className="text-sm text-white/60">{selectedBanner.name}</span>}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-white/70 mb-2">
              Background Image{" "}
              <span className="text-xs text-white/40 font-normal">(Optional)</span>
            </label>
            <div className="flex items-center gap-4">
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setSelectedBackground(e.target.files[0])}
                className="hidden"
                id="background-upload"
              />
              <label
                htmlFor="background-upload"
                className="px-4 py-2 bg-white/10 hover:bg-white/15 border border-white/20 rounded-lg cursor-pointer transition-colors flex items-center gap-2"
              >
                <Camera size={18} />
                Choose File
              </label>
              {selectedBackground && (
                <span className="text-sm text-white/60">{selectedBackground.name}</span>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 p-6 border-t border-white/10">
          <button
            onClick={onClose}
            className="px-6 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onSave}
            className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-cyan-500 to-purple-500 hover:from-cyan-600 hover:to-purple-600 transition-all"
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProfileHub;